import moment from "moment-timezone";
import {
  GetTransactionsResponse,
  TransactionResponse,
  CalendarEvent,
  BookedService,
} from "../types/transactions";
import {
  calculateDiscountedItemPrice,
  getLineDiscountDollars,
  hasLineDiscount,
} from "./discountUtils";
import { LineDiscountEntry } from "../types/discounts";
import {
  buildDiscountableLines,
  discountGroupIdOf,
  DiscountableLineKind,
  DiscountGroup,
  groupLineDiscounts,
} from "./lineItemDiscounts";

export const transformTransactionsToEvents = (
  transactions: GetTransactionsResponse[],
  timezone: string,
  salonServices?: any[],
): CalendarEvent[] => {
  const events: CalendarEvent[] = [];

  transactions.forEach((transaction) => {
    // Create separate events for each booked service
    transaction.line_items.booked_services.forEach((bookedService) => {
      const event = mapBookedServiceToEvent(
        bookedService,
        transaction,
        timezone,
        salonServices,
      );
      events.push(event);
    });
  });

  return events;
};

export const transformTransactionToEvent = (
  transaction: TransactionResponse | GetTransactionsResponse,
  timezone: string,
  salonServices?: any[],
): CalendarEvent[] => {
  const events: CalendarEvent[] = [];

  transaction.line_items.booked_services.forEach((bookedService) => {
    const event = mapBookedServiceToEvent(
      bookedService,
      transaction,
      timezone,
      salonServices,
    );
    events.push(event);
  });

  return events;
};

export const mapBookedServiceToEvent = (
  bookedService: BookedService,
  transaction: GetTransactionsResponse | TransactionResponse,
  timezone: string,
  salonServices?: any[],
): CalendarEvent => {
  // Convert GMT datetime to salon's timezone
  const start = moment
    .utc(bookedService.start_dt)
    .tz(timezone)
    .format("YYYY-MM-DD HH:mm");
  const end = moment
    .utc(bookedService.calc__end_dt)
    .tz(timezone)
    .format("YYYY-MM-DD HH:mm");

  // Use client data from transaction
  const client = "client" in transaction ? transaction.client : null;
  const firstName = client?.first_name || "";
  const lastName = client?.last_name || "";
  const email = client?.email || "";
  const phone = client ? `${client.area_code}${client.phone_number}` : "";

  // Create title with service name and client name
  const title = `${bookedService.name} - ${firstName} ${lastName}`;

  const response = {
    id: bookedService.id, // Use booked service ID for calendar events
    title,
    start: new Date(start),
    end: new Date(end),
    backgroundColor:
      salonServices?.find((s: any) => s.id === bookedService.salon_service_id)
        ?.default_calendar_event_color ||
      bookedService.calendar_event_color ||
      "#36AFC9",
    duration:
      (new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60),
    baseDuration:
      (bookedService.base_duration_mins ?? 30) +
      (bookedService.sub_line_items?.reduce(
        (sum, item) => sum + (item.duration_delta_mins || 0),
        0,
      ) || 0),
    processingTime: bookedService.processing_time_mins ?? 0,
    finishingTime: bookedService.finishing_time_mins ?? 0,
    bufferTime: bookedService.buffer_time_mins ?? 0,
    enableDoubleBooking: bookedService.enable_double_booking ?? false,
    price: bookedService.unit_price,
    firstName,
    lastName,
    phone,
    email,
    salonServiceId: bookedService.salon_service_id,
    serviceType: bookedService.service_type,
    employeeName: bookedService.staff_name,
    employeeId: bookedService.staff_id,
    resourceId: bookedService.staff_id, // Required for column assignment
    status: transaction.transaction.appointment_status || "",
    // Customer booking notes now live on the client profile (client notes), not
    // on the transaction. This vestigial field is no longer populated and is
    // slated for removal in Phase 2.
    customerNotes: "",
    isOptedInReminder: client?.is_opted_in_reminder || false,
    clientUuid: client?.id || "",
    transactionId: transaction.transaction.id,
    lineItemId: bookedService.line_item_id,
    transactionStatus: transaction.transaction.transaction_status,
    payments: transaction.payments,
    bookedUsingAnyAvailable:
      transaction.transaction.booked_using_any_available ?? false,
    createdNewClient: transaction.transaction.created_new_client ?? false,
  };

  return response;
};

export const groupTransactionsByDate = (
  transactions: GetTransactionsResponse[],
  selectedDate: Date,
  selectedView: string,
) => {
  const now = moment();
  const startOfView = moment(selectedDate).startOf(
    selectedView === "day" ? "day" : "week",
  );
  const endOfView = moment(selectedDate).endOf(
    selectedView === "day" ? "day" : "week",
  );

  // Filter transactions to only show those within the current view
  const viewTransactions = transactions.filter((transaction) => {
    // Check if any booked service falls within the view range
    return transaction.line_items.booked_services.some((service) => {
      const serviceDate = moment.utc(service.start_dt);
      return serviceDate.isBetween(startOfView, endOfView, null, "[]");
    });
  });

  return {
    "Needs Accepting": viewTransactions.filter(
      (transaction) =>
        transaction.transaction.appointment_status === "Requested",
    ),
    Upcoming: viewTransactions.filter(
      (transaction) =>
        (transaction.transaction.appointment_status === "Accepted" ||
          transaction.transaction.appointment_status === "Confirmed") &&
        transaction.line_items.booked_services.some((service) =>
          moment.utc(service.calc__end_dt).isAfter(now),
        ),
    ),
    Past: viewTransactions
      .filter(
        (transaction) =>
          transaction.line_items.booked_services.every(
            (service) =>
              moment.utc(service.calc__end_dt).isBefore(now) ||
              transaction.transaction.appointment_status === "Completed" ||
              transaction.transaction.appointment_status === "No Show",
          ) && transaction.transaction.appointment_status !== "Cancelled",
      )
      .sort((a, b) => {
        const aLatestService = a.line_items.booked_services.reduce(
          (latest, service) =>
            moment.utc(service.calc__end_dt).isAfter(latest)
              ? moment.utc(service.calc__end_dt)
              : latest,
          moment.utc(a.line_items.booked_services[0]?.calc__end_dt || ""),
        );
        const bLatestService = b.line_items.booked_services.reduce(
          (latest, service) =>
            moment.utc(service.calc__end_dt).isAfter(latest)
              ? moment.utc(service.calc__end_dt)
              : latest,
          moment.utc(b.line_items.booked_services[0]?.calc__end_dt || ""),
        );
        return bLatestService.valueOf() - aLatestService.valueOf();
      }),
  };
};

export const calculateTransactionPaidAmount = (
  transaction: GetTransactionsResponse | TransactionResponse,
): number => {
  return transaction.payments
    .filter((payment) => payment.status === "succeeded")
    .reduce((sum, payment) => sum + payment.amount, 0);
};

export const formatTransactionTime = (
  transaction: GetTransactionsResponse | TransactionResponse,
  timezone: string,
): string => {
  const firstService = transaction.line_items.booked_services[0];
  if (!firstService) return "Unknown time";

  const startTime = moment
    .utc(firstService.start_dt)
    .tz(timezone)
    .format("h:mm A");
  const endTime = moment
    .utc(firstService.calc__end_dt)
    .tz(timezone)
    .format("h:mm A");

  return `${startTime} - ${endTime}`;
};

export const transformTransactionResponseToGetTransactionsResponse = (
  transactionResponse: TransactionResponse,
): GetTransactionsResponse => {
  return {
    ...transactionResponse,
    reminder_messages: [], // Empty array for new appointments
  };
};

// ---------------------------------------------------------------------------
// Discount groups (summary rows)
// ---------------------------------------------------------------------------

export interface GroupedDiscountItem {
  /** Booked service / product id; a package's line_item_id. */
  id: string;
  line_item_id: string;
  name: string;
  type: DiscountableLineKind;
  /** Line price before any discount. */
  originalPrice: number;
  /** Line price after ALL its discounts (it may carry several). */
  discountedPrice: number;
  /** This group's discount as applied on the line. */
  discount: LineDiscountEntry;
}

/** A discount applied across one or more lines of a transaction. */
export interface GroupedDiscount extends DiscountGroup {
  items: GroupedDiscountItem[];
}

interface LinePricing {
  id: string;
  originalPrice: number;
  discountedPrice: number;
}

const linePricingByKey = (
  transaction: GetTransactionsResponse | TransactionResponse,
): Map<string, LinePricing> => {
  const pricing = new Map<string, LinePricing>();
  transaction.line_items.booked_services.forEach((service) => {
    pricing.set(service.line_item_id, {
      id: service.id,
      originalPrice: service.calc__cost_excl_tips_discounts,
      discountedPrice: calculateDiscountedItemPrice(service),
    });
  });
  transaction.line_items.products.forEach((product) => {
    pricing.set(product.line_item_id, {
      id: product.id,
      originalPrice: product.unit_price * product.quantity,
      discountedPrice: calculateDiscountedItemPrice(product, product.quantity),
    });
  });
  // A package's base is its unit_price (the catalog/bundle price); packages
  // have no separate `id`, so it mirrors line_item_id.
  (transaction.line_items.service_packages ?? []).forEach((pkg) => {
    pricing.set(pkg.line_item_id, {
      id: pkg.line_item_id,
      originalPrice: pkg.unit_price,
      discountedPrice: Math.max(
        0,
        pkg.unit_price - getLineDiscountDollars(pkg, pkg.unit_price),
      ),
    });
  });
  return pricing;
};

/**
 * One entry per distinct discount on the transaction — per preset, or for
 * unlinked discounts (no preset) per name + type + amount — listing every line
 * it applies to. Order: first appearance across services, products, then
 * packages.
 */
export const calculateGroupedDiscounts = (
  transaction: GetTransactionsResponse | TransactionResponse,
): GroupedDiscount[] => {
  const lines = buildDiscountableLines(transaction);
  const linesByKey = new Map(lines.map((line) => [line.key, line]));
  const pricing = linePricingByKey(transaction);

  return groupLineDiscounts(lines).map((group) => ({
    ...group,
    items: group.lineKeys.map((lineKey) => {
      const line = linesByKey.get(lineKey)!;
      const linePricing = pricing.get(lineKey)!;
      const entry =
        line.discounts.find((d) => discountGroupIdOf(d) === group.id) ??
        line.discounts[0];
      return {
        id: linePricing.id,
        line_item_id: lineKey,
        name: line.name,
        type: line.kind,
        originalPrice: linePricing.originalPrice,
        discountedPrice: linePricing.discountedPrice,
        discount: entry,
      };
    }),
  }));
};

export const calculateDiscountedServiceTotal = (
  transaction: GetTransactionsResponse | TransactionResponse,
): number => {
  let total = transaction.line_items.booked_services.reduce(
    (sum, service) => sum + service.calc__cost_excl_tips_discounts,
    0,
  );

  // Apply line item discounts first
  transaction.line_items.booked_services.forEach((service) => {
    if (hasLineDiscount(service)) {
      total =
        total -
        service.calc__cost_excl_tips_discounts +
        calculateDiscountedItemPrice(service);
    }
  });

  return Math.max(0, total); // Ensure total doesn't go negative
};

export const calculateDiscountedProductTotal = (
  transaction: GetTransactionsResponse | TransactionResponse,
): number => {
  let total = transaction.line_items.products.reduce(
    (sum, product) => sum + product.unit_price * product.quantity,
    0,
  );

  // Apply line item discounts first
  transaction.line_items.products.forEach((product) => {
    if (hasLineDiscount(product)) {
      total =
        total -
        product.unit_price * product.quantity +
        calculateDiscountedItemPrice(product, product.quantity);
    }
  });

  return Math.max(0, total); // Ensure total doesn't go negative
};

/**
 * Appointment statuses that indicate a transaction is in a final/complete state.
 * Used for payroll sync validation and transaction filtering.
 */
export const FINAL_APPOINTMENT_STATUSES: string[] = [
  "Completed",
  "Declined",
  "No Show",
  "Cancelled",
];

/**
 * Checks if a transaction is in a final state (completed or cancelled).
 * Quick sales (transactions without booked services) are always considered complete.
 *
 * @param transaction - The transaction to check
 * @returns true if the transaction is in a final state
 */
export const isTransactionInFinalState = (
  transaction: GetTransactionsResponse | TransactionResponse,
): boolean => {
  const lineItems = transaction.line_items;
  const isQuickSale =
    !lineItems.booked_services || lineItems.booked_services.length === 0;

  // Quick sales are always considered "complete"
  if (isQuickSale) return true;

  const appointmentStatus = transaction.transaction.appointment_status;
  return (
    !!appointmentStatus &&
    FINAL_APPOINTMENT_STATUSES.includes(appointmentStatus)
  );
};
